// Google Analytics 4 tracking utilities

declare global {
  interface Window {
    gtag: {
      (command: 'js', date: Date): void;
      (command: 'config', measurementId: string, config?: Record<string, unknown>): void;
      (command: 'event', eventName: string, config?: Record<string, unknown>): void;
    };
    dataLayer: unknown[];
  }
}

type GoogleAdsAuthEvent = 'sign_up' | 'auth_success' | 'login';

type GoogleAdsEventOptions = {
  redirectUrl?: string;
  onComplete?: () => void;
  params?: Record<string, unknown>;
};

const configuredMeasurementId = import.meta.env.VITE_GA_MEASUREMENT_ID;
export const GA_MEASUREMENT_ID =
  configuredMeasurementId && /^G-[A-Z0-9]+$/.test(configuredMeasurementId)
    ? configuredMeasurementId
    : '';

const configuredGoogleAdsId = import.meta.env.VITE_GOOGLE_ADS_ID;
export const GOOGLE_ADS_ID =
  configuredGoogleAdsId && /^AW-[0-9]+$/.test(configuredGoogleAdsId)
    ? configuredGoogleAdsId
    : '';

const configuredGoogleAdsSignUpLabel = import.meta.env.VITE_GOOGLE_ADS_SIGN_UP_LABEL;
const configuredGoogleAdsLoginLabel = import.meta.env.VITE_GOOGLE_ADS_LOGIN_LABEL;
const configuredGoogleAdsAuthSuccessLabel = import.meta.env.VITE_GOOGLE_ADS_AUTH_SUCCESS_LABEL;
const configuredGoogleAdsGenerateLeadLabel = import.meta.env.VITE_GOOGLE_ADS_GENERATE_LEAD_LABEL;

const withAdsSendTo = (eventName: GoogleAdsAuthEvent, params?: Record<string, unknown>) => {
  if (!GOOGLE_ADS_ID) return params || {};

  const conversionLabel =
    eventName === 'sign_up'
      ? configuredGoogleAdsSignUpLabel
      : eventName === 'login'
      ? configuredGoogleAdsLoginLabel
      : configuredGoogleAdsAuthSuccessLabel;

  if (!conversionLabel) return params || {};

  return {
    ...(params || {}),
    send_to: `${GOOGLE_ADS_ID}/${conversionLabel}`,
  };
};

const isDev = import.meta.env.DEV;

// In dev mode, wrap window.gtag so every 'event' call is also logged
// to the browser console — no-op in production.
const wrapGtagForDevLogging = () => {
  if (!isDev || typeof window === 'undefined' || typeof window.gtag === 'undefined') return;

  const _original = window.gtag;
  window.gtag = ((...args: Parameters<Window['gtag']>) => {
    if (args[0] === 'event') {
      const [, name, params] = args as ['event', string, Record<string, unknown> | undefined];
      // Log to console
      console.groupCollapsed(
        `%c[GA4]%c ${name}`,
        'background:#4285F4;color:#fff;padding:2px 5px;border-radius:3px;font-weight:bold',
        'color:#202124;font-weight:bold'
      );
      if (params && Object.keys(params).length > 0) console.table(params);
      console.groupEnd();
      // Emit to floating dev panel
      window.dispatchEvent(new CustomEvent('ga4:event', { detail: { name, params: params ?? {} } }));
    }
    return (_original as (...a: unknown[]) => void)(...(args as unknown[]));
  }) as Window['gtag'];
};

let gaInitialized = false;

export const initializeGA4 = () => {
  if (gaInitialized || (!GA_MEASUREMENT_ID && !GOOGLE_ADS_ID) || typeof window === 'undefined') return;

  if (typeof window.gtag !== 'undefined') {
    if (GA_MEASUREMENT_ID) {
      window.gtag('config', GA_MEASUREMENT_ID, {
        send_page_view: true,
      });
    }

    if (GOOGLE_ADS_ID) {
      window.gtag('config', GOOGLE_ADS_ID);
    }

    gaInitialized = true;
    wrapGtagForDevLogging();
    return;
  }

  const primaryTagId = GA_MEASUREMENT_ID || GOOGLE_ADS_ID;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${primaryTagId}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  function gtag(...args: unknown[]) {
    window.dataLayer.push(args);
  }

  window.gtag = gtag as Window['gtag'];

  window.gtag('js', new Date());
  if (GA_MEASUREMENT_ID) {
    window.gtag('config', GA_MEASUREMENT_ID, {
      send_page_view: true,
    });
  }

  if (GOOGLE_ADS_ID) {
    window.gtag('config', GOOGLE_ADS_ID);
  }

  gaInitialized = true;
  wrapGtagForDevLogging();
};

// Track page views
export const trackPageView = (url: string, title?: string) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_path: url,
      page_title: title,
    });
  }
};

// Track custom events
export const trackEvent = (
  action: string,
  category: string,
  label?: string,
  value?: number
) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value,
    });
  }
};

// Track PWA lifecycle and installation events
export const trackPWAEvent = (
  action:
    | 'sw_registered'
    | 'offline_ready'
    | 'update_available'
    | 'install_prompt_available'
    | 'install_prompt_accepted'
    | 'install_prompt_dismissed'
    | 'installed'
    | 'opened_standalone',
  details?: string
) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', `pwa_${action}`, {
      event_category: 'pwa',
      event_label: details,
    });
  }
};

// ============================================
// SCROLL & ENGAGEMENT TRACKING
// ============================================

// Track scroll depth milestones (25%, 50%, 75%, 90%, 100%)
export const trackScrollDepth = (percentage: number, pageName: string) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'scroll_depth', {
      event_category: 'engagement',
      event_label: pageName,
      value: percentage,
      scroll_percentage: percentage,
    });
  }
};

// Track section visibility (when user sees a section)
export const trackSectionView = (sectionName: string, pageName: string, position?: number) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'section_view', {
      event_category: 'engagement',
      event_label: sectionName,
      page_name: pageName,
      section_name: sectionName,
      ...(position !== undefined && { section_position: position }),
    });
  }
};

// Track time on page
export const trackTimeOnPage = (seconds: number, pageName: string) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'time_on_page', {
      event_category: 'engagement',
      event_label: pageName,
      value: seconds,
      engagement_time_seconds: seconds,
    });
  }
};

// ============================================
// CONVERSION FUNNEL TRACKING
// ============================================

// Track pricing section interactions
export const trackPricingInteraction = (
  action: 'view' | 'hover' | 'click_plan' | 'compare',
  planId?: 'free' | 'starter' | 'pro' | 'business',
  details?: string
) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', `pricing_${action}`, {
      event_category: 'conversion_funnel',
      event_label: planId || details,
      plan_id: planId,
    });
  }
};

// Track CTA button clicks
export const trackCTAClick = (
  buttonLabel: string,
  location: string,
  destination?: string
) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'cta_click', {
      event_category: 'conversion_funnel',
      event_label: buttonLabel,
      cta_location: location,
      destination: destination,
    });
  }
};

// Track header/mobile nav link clicks
export const trackNavLinkClick = (
  label: string,
  destination: string,
  source: 'desktop_nav' | 'mobile_menu'
) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'nav_link_click', {
      event_category: 'navigation',
      event_label: label,
      nav_item_label: label,
      nav_item_destination: destination,
      nav_source: source,
    });
  }
};

// Track mobile menu open / close
export const trackMobileMenuToggle = (open: boolean) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', open ? 'mobile_menu_open' : 'mobile_menu_close', {
      event_category: 'navigation',
    });
  }
};

// Track form interactions
export const trackFormInteraction = (
  formName: string,
  action: 'start' | 'field_focus' | 'field_blur' | 'submit' | 'error' | 'abandon',
  fieldName?: string,
  errorMessage?: string
) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', `form_${action}`, {
      event_category: 'forms',
      event_label: formName,
      form_name: formName,
      field_name: fieldName,
      error_message: errorMessage,
    });
  }
};

// ============================================
// AUTH FUNNEL TRACKING
// ============================================

// Track auth page interactions
export const trackAuthFunnel = (
  action: 'page_view' | 'switch_mode' | 'form_start' | 'form_submit' | 'success' | 'error' | 'abandon',
  mode: 'login' | 'signup',
  method?: 'email' | 'google' | 'apple',
  errorType?: string
) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', `auth_${action}`, {
      event_category: 'auth_funnel',
      event_label: mode,
      auth_mode: mode,
      auth_method: method,
      error_type: errorType,
    });
  }
};

// Track subscription events
export const trackSubscription = (
  planId: 'starter' | 'pro' | 'business',
  action: 'initiated' | 'completed' | 'canceled'
) => {
  const planPrices = {
    starter: 12,
    pro: 24,
    business: 120,
  };

  if (typeof window.gtag !== 'undefined') {
    if (action === 'completed') {
      // Track as purchase/conversion
      window.gtag('event', 'purchase', {
        transaction_id: `sub_${Date.now()}`,
        value: planPrices[planId],
        currency: 'USD',
        items: [
          {
            item_id: planId,
            item_name: `${planId.charAt(0).toUpperCase() + planId.slice(1)} Plan`,
            category: 'subscription',
            price: planPrices[planId],
            quantity: 1,
          },
        ],
      });
    }

    // Also track as custom event for more flexibility
    window.gtag('event', `subscription_${action}`, {
      event_category: 'subscription',
      event_label: planId,
      value: planPrices[planId],
    });
  }
};

// Track checkout initiation
export const trackCheckoutInitiated = (planId: 'starter' | 'pro' | 'business') => {
  const planPrices = {
    starter: 12,
    pro: 24,
    business: 120,
  };

  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'begin_checkout', {
      currency: 'USD',
      value: planPrices[planId],
      items: [
        {
          item_id: planId,
          item_name: `${planId.charAt(0).toUpperCase() + planId.slice(1)} Plan`,
          category: 'subscription',
          price: planPrices[planId],
          quantity: 1,
        },
      ],
    });
  }
};

// Track sign up
export const trackSignUp = (method: 'email' | 'google' | 'apple') => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'sign_up', withAdsSendTo('sign_up', {
      method: method,
    }));
  }
};

// Track login
export const trackLogin = (method: 'email' | 'google' | 'apple') => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'login', withAdsSendTo('login', {
      method: method,
    }));
  }
};

export const trackSocialAuth = (
  action: 'signup' | 'login',
  provider: 'google' | 'apple'
) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', `social_${action}`, {
      event_category: 'auth_funnel',
      auth_method: provider,
      method: provider,
    });
  }
};

export const trackLeadGenerated = (
  source: string,
  leadId?: string,
  subject?: string
) => {
  if (typeof window.gtag !== 'undefined') {
    const sendTo =
      GOOGLE_ADS_ID && configuredGoogleAdsGenerateLeadLabel
        ? { send_to: `${GOOGLE_ADS_ID}/${configuredGoogleAdsGenerateLeadLabel}` }
        : {};
    window.gtag('event', 'generate_lead', {
      event_category: 'lead_capture',
      lead_source: source,
      form_name: source,
      subject,
      lead_id: leadId,
      value: 1,
      currency: 'USD',
      ...sendTo,
    });
  }
};

export const trackGoogleAdsAuthEvent = (
  eventName: GoogleAdsAuthEvent,
  options?: GoogleAdsEventOptions
) => {
  const { redirectUrl, onComplete, params } = options || {};

  let completed = false;
  const complete = () => {
    if (completed) return;
    completed = true;

    if (typeof onComplete === 'function') {
      onComplete();
    }

    if (typeof redirectUrl === 'string') {
      window.location.assign(redirectUrl);
    }
  };

  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', eventName, withAdsSendTo(eventName, {
      event_callback: complete,
      event_timeout: 2000,
      ...params,
    }));

    if (redirectUrl || onComplete) {
      window.setTimeout(complete, 2100);
    }

    return false;
  }

  complete();
  return false;
};

export const trackAuthSuccess = (method: 'email' | 'google' | 'apple', options?: GoogleAdsEventOptions) => {
  trackGoogleAdsAuthEvent('auth_success', {
    ...options,
    params: {
      method,
      ...(options?.params || {}),
    },
  });
};

// ============================================
// FEATURE USAGE TRACKING
// ============================================

// Track feature interaction (for understanding what users try to use)
export const trackFeatureInteraction = (
  featureName: string,
  action: 'view' | 'click' | 'blocked' | 'upgrade_prompt',
  planRequired?: string
) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', `feature_${action}`, {
      event_category: 'feature_usage',
      event_label: featureName,
      feature_name: featureName,
      plan_required: planRequired,
    });
  }
};

// Track editor usage
export const trackEditorAction = (
  action: 'add_link' | 'edit_link' | 'delete_link' | 'change_theme' | 'update_profile' | 'upload_avatar',
  details?: string
) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', `editor_${action}`, {
      event_category: 'editor_usage',
      event_label: details,
    });
  }
};

/**
 * Link creation funnel — tracks intent vs completion to surface UX friction.
 *
 * Intent gap: `link_form_opened` fires when the user clicks "Add Link".
 * If `link_added` never follows in the same session, the user abandoned the form.
 *
 * Difficulty signals:
 * - `link_form_validation_error` → user doesn't know the expected format (URL / title)
 * - `link_form_cancelled` → saw the form but gave up
 * - `link_add_error` → backend/save failure (infra issue)
 * - `link_limit_reached` → plan friction (upgrade opportunity)
 *
 * Engagement signals:
 * - `link_added` → successful creation  (first one also fires `activation_first_link_created`)
 * - `link_deleted` → churn signal if happens quickly after add
 * - `link_toggled` → user actively managing their bio
 */
export const trackLinkFormEvent = (
  action:
    | 'form_opened'          // user clicked "Add Link" button
    | 'form_cancelled'       // user dismissed form without saving
    | 'validation_error'     // form blocked due to invalid input
    | 'add_error'            // backend save failed
    | 'limit_reached'        // plan link limit hit (gated)
    | 'added'                // link successfully created
    | 'deleted'              // link deleted
    | 'toggled',             // link visibility toggle (enabled/disabled)
  params?: {
    total_links?: number;    // how many links the user has at the time
    is_first_link?: boolean; // true when this is the very first link created
    error_field?: string;    // 'title' | 'url' — which field failed validation
    source?: string;         // 'link_editor' | 'quick_links'
  }
) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', `link_${action}`, {
      event_category: 'link_creation_funnel',
      ...params,
    });
  }
};

// ============================================
// AI CHAT FUNNEL TRACKING
// ============================================

/**
 * Visitor-side chat events (public bio / chat page).
 * These measure whether the AI chat widget generates value for Business plan owners.
 * Key "aha moment": chat_message_sent — a real visitor interacted with the owner's agent.
 */
export const trackChatWidgetEvent = (
  action:
    | 'rendered'       // widget appeared on public bio page (agent valid + enabled)
    | 'opened'         // visitor clicked FAB to open chat panel
    | 'closed'         // visitor manually closed chat panel
    | 'expanded'       // visitor expanded to fullscreen
    | 'minimized'      // visitor minimized from fullscreen
    | 'session_started' // visitor submitted pre-chat form (or sent first msg directly)
    | 'message_sent'   // visitor sent a message to agent — core engagement event
    | 'session_ended'  // visitor explicitly ended conversation
    | 'rate_limited'   // visitor hit per-IP rate limit
    | 'limit_reached', // conversation ended by message-count limit (410)
  params?: {
    agent_id?: string;
    source?: 'widget' | 'page'; // widget = ChatWidget.tsx, page = PublicChat.tsx
    message_count?: number;     // total messages in session so far
    has_visitor_info?: boolean; // did visitor provide name/email?
  }
) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', `chat_${action}`, {
      event_category: 'ai_chat',
      ...params,
    });
  }
};

/**
 * Owner-side chat configuration events (dashboard).
 * These measure AI chat feature adoption — the strongest retention signal for Business plan.
 */
export const trackChatAgentEvent = (
  action:
    | 'configured'     // owner saved agent settings (any update)
    | 'created'        // owner created agent for the first time (activation milestone)
    | 'enabled'        // owner toggled agent on
    | 'disabled'       // owner toggled agent off
    | 'prompt_saved',  // owner saved the AI system prompt
  params?: {
    has_prompt?: boolean;
    has_welcome_message?: boolean;
    require_visitor_info?: boolean;
  }
) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', `chat_agent_${action}`, {
      event_category: 'ai_chat_setup',
      ...params,
    });
  }
};

/**
 * Upgrade intent events from within the AI chat feature.
 * Non-Business users who discover the chat feature and are prompted to upgrade.
 */
export const trackChatUpgradeIntent = (
  action: 'prompt_shown' | 'cta_clicked' | 'dismissed',
  currentPlan?: string
) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', `chat_upgrade_${action}`, {
      event_category: 'upgrade_intent',
      event_label: 'ai_chat',
      feature: 'ai_chat',
      current_plan: currentPlan,
    });
  }
};

// ============================================
// ACTIVATION FUNNEL TRACKING
// ============================================

/**
 * Post-signup activation milestones.
 * These are the key events between sign_up and first paid conversion.
 * Low activation rates are a primary churn driver — users who don't reach these
 * milestones are very likely to churn before ever paying.
 *
 * Recommended GA4 funnel order:
 *   sign_up → activation_first_link → activation_page_live → activation_chat_configured → purchase
 */
export const trackActivation = (
  milestone:
    | 'first_link_created'   // user added their first bio link
    | 'page_live'            // user's public page was first visited (or first publish action)
    | 'link_shared'          // user copied their bio link or clicked "share"
    | 'chat_configured',     // user configured AI agent for the first time (major retention signal)
  params?: { plan?: string; method?: string }
) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', `activation_${milestone}`, {
      event_category: 'activation',
      ...params,
    });
  }
};

// ============================================
// EXIT INTENT & ABANDONMENT
// ============================================

// Track exit intent
export const trackExitIntent = (pageName: string, scrollPercentage: number) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'exit_intent', {
      event_category: 'engagement',
      event_label: pageName,
      scroll_percentage: scrollPercentage,
    });
  }
};

// Track page abandonment (when user leaves without converting)
export const trackPageAbandonment = (
  pageName: string,
  timeOnPage: number,
  lastInteraction?: string
) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'page_abandonment', {
      event_category: 'engagement',
      event_label: pageName,
      time_on_page: timeOnPage,
      last_interaction: lastInteraction,
    });
  }
};
